import nacl from 'tweetnacl';
import { decodeUTF8, encodeUTF8, encodeBase64, decodeBase64 } from 'tweetnacl-util';

const KEYPAIR_STORAGE_KEY = 'chat_keypair';
const CONVERSATION_KEY_PREFIX = 'chat_conversation_key_';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const toUint8Array = (value) => {
  if (value instanceof Uint8Array) return value;
  if (Array.isArray(value)) return new Uint8Array(value);
  return new Uint8Array(value);
};

export function getStoredKeyPair() {
  const stored = localStorage.getItem(KEYPAIR_STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    return {
      publicKey: decodeBase64(parsed.publicKey),
      secretKey: decodeBase64(parsed.secretKey),
    };
  } catch (error) {
    console.error('Failed to parse chat keypair', error);
    return null;
  }
}

export function storeKeyPair(keyPair) {
  localStorage.setItem(
    KEYPAIR_STORAGE_KEY,
    JSON.stringify({
      publicKey: encodeBase64(keyPair.publicKey),
      secretKey: encodeBase64(keyPair.secretKey),
    })
  );
}

export function ensureKeyPair() {
  let keyPair = getStoredKeyPair();
  if (!keyPair) {
    keyPair = nacl.box.keyPair();
    storeKeyPair(keyPair);
  }
  return keyPair;
}

export function getConversationKey(conversationId) {
  const stored = localStorage.getItem(CONVERSATION_KEY_PREFIX + conversationId);
  if (!stored) return null;
  return decodeBase64(stored);
}

export function setConversationKey(conversationId, keyBytes) {
  localStorage.setItem(CONVERSATION_KEY_PREFIX + conversationId, encodeBase64(keyBytes));
}

export function generateConversationKey() {
  return nacl.randomBytes(32);
}

export function encryptConversationKeyForUser(conversationKeyBytes, recipientPublicKeyBase64) {
  const recipientPublicKey = decodeBase64(recipientPublicKeyBase64);
  const ephemeralKeyPair = nacl.box.keyPair();
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const cipher = nacl.box(conversationKeyBytes, nonce, recipientPublicKey, ephemeralKeyPair.secretKey);

  const payload = new Uint8Array(
    ephemeralKeyPair.publicKey.length + nonce.length + cipher.length
  );
  payload.set(ephemeralKeyPair.publicKey, 0);
  payload.set(nonce, ephemeralKeyPair.publicKey.length);
  payload.set(cipher, ephemeralKeyPair.publicKey.length + nonce.length);

  return encodeBase64(payload);
}

export function decryptConversationKeyShare(encryptedShareBase64, recipientKeyPair) {
  if (!encryptedShareBase64) return null;
  const payload = decodeBase64(encryptedShareBase64);
  const publicKey = payload.slice(0, nacl.box.publicKeyLength);
  const nonce = payload.slice(nacl.box.publicKeyLength, nacl.box.publicKeyLength + nacl.box.nonceLength);
  const cipher = payload.slice(nacl.box.publicKeyLength + nacl.box.nonceLength);
  const message = nacl.box.open(cipher, nonce, publicKey, recipientKeyPair.secretKey);
  return message || null;
}

export function encryptPayload(conversationKeyBytes, plaintext) {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const messageBytes = textEncoder.encode(plaintext);
  const cipher = nacl.secretbox(messageBytes, nonce, conversationKeyBytes);
  const payload = new Uint8Array(nonce.length + cipher.length);
  payload.set(nonce, 0);
  payload.set(cipher, nonce.length);
  return encodeBase64(payload);
}

export function decryptPayload(conversationKeyBytes, ciphertextBase64) {
  if (!ciphertextBase64) return '';
  const payload = decodeBase64(ciphertextBase64);
  const nonce = payload.slice(0, nacl.secretbox.nonceLength);
  const cipher = payload.slice(nacl.secretbox.nonceLength);
  const message = nacl.secretbox.open(cipher, nonce, conversationKeyBytes);
  if (!message) return '';
  return textDecoder.decode(message);
}

export async function encryptFile(conversationKeyBytes, file) {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const cipher = nacl.secretbox(bytes, nonce, conversationKeyBytes);
  const payload = new Uint8Array(nonce.length + cipher.length);
  payload.set(nonce, 0);
  payload.set(cipher, nonce.length);
  return new File([payload], `${file.name}.enc`, {
    type: 'application/octet-stream',
  });
}

export function decryptFile(conversationKeyBytes, encryptedBuffer) {
  const payload = new Uint8Array(encryptedBuffer);
  const nonce = payload.slice(0, nacl.secretbox.nonceLength);
  const cipher = payload.slice(nacl.secretbox.nonceLength);
  return nacl.secretbox.open(cipher, nonce, conversationKeyBytes);
}

export const keyToBase64 = (bytes) => encodeBase64(bytes);
export const base64ToKey = (value) => decodeBase64(value);


